import { APIError, APITypes, StreemAPI } from '@streem/api';
import { action, autorun, computed, IReactionDisposer } from 'mobx';

import { APICallStore } from './api_call_store';
import { CompanySettingsStore } from './company_settings_store';

export class NoteTemplatesStore {
    private templatesStore = new APICallStore(
        StreemAPI.companies.getNoteTemplates.bind(StreemAPI.companies),
        'listNotesTemplates',
    );

    private disposer: IReactionDisposer | null = null;

    constructor(
        private readonly companySettingsStore: CompanySettingsStore,
        private readonly companyCode: string,
    ) {
        this.disposer = this.registerLoader();
    }

    public disconnect(): void {
        typeof this.disposer === 'function' && this.disposer();
        this.disposer = null;
    }

    @computed
    public get complete(): boolean {
        // complete when the companySettingsStore is complete and either notes templates fetch is complete
        //      or the notes templates feature flag is disabled
        return (
            this.companySettingsStore.complete &&
            (this.templatesStore.complete || !this.companySettingsStore.notesTemplatesEnabled)
        );
    }

    @computed
    public get lastError(): APIError | undefined {
        return this.templatesStore.lastError;
    }

    @computed
    public get loading(): boolean {
        return (
            this.companySettingsStore.loading ||
            (this.companySettingsStore.notesTemplatesEnabled && this.templatesStore.loading)
        );
    }

    @computed
    public get noteTemplates(): APITypes.StreemApiNoteTemplate[] | undefined {
        if (!this.templatesStore.response?.noteTemplates) {
            return undefined;
        }
        return this.templatesStore.response?.noteTemplates;
    }

    @action
    private registerLoader(): IReactionDisposer {
        return autorun(() => {
            if (
                this.companySettingsStore.complete &&
                this.companySettingsStore.notesTemplatesEnabled
            ) {
                this.templatesStore.fetch(this.companyCode);
            }
        });
    }

    // expose for testing
    public get _templatesStore(): typeof this.templatesStore {
        return this.templatesStore;
    }
}
